import { useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { isPremium } from "../lib/plans";

/**
 * Wraps a premium-only page with a blur overlay + upgrade CTA when the
 * current user is not on the premium tier. Renders children unchanged for
 * premium/enterprise users.
 *
 * Why a blur rather than a hard redirect? Users need to see *what they're
 * missing* — the feature's UI behind the scrim is itself the sales pitch.
 * This is the same pattern Linear, Superhuman, and Notion use for gated
 * features, and it consistently outperforms redirect-to-upgrade in
 * conversion tests because the value is tangible, not hypothetical.
 */
export default function PremiumGate({ user, featureName = "this feature", featureCopy, children }) {
  const navigate = useNavigate();

  // Premium (or legacy "paid", or enterprise) users see the real page.
  if (isPremium(user)) return children;

  const handleUpgrade = () => {
    // Route to the pricing page rather than firing Stripe directly so the
    // user can compare tiers before committing. The pricing page handles
    // the Stripe checkout redirect itself.
    navigate("/pricing");
  };

  return (
    <div className="relative min-h-[calc(100vh-100px)]" data-testid="premium-gate">
      {/* Blurred real page underneath — users see their actual data faintly
          so the feature feels tangible rather than abstract. */}
      <div
        aria-hidden
        className="select-none"
        style={{
          filter: "blur(5px)",
          pointerEvents: "none",
          opacity: 0.55,
        }}
      >
        {children}
      </div>

      {/* Upgrade card floating above. Uses absolute positioning inside the
          already-relative wrapper so the scroll-position follows the page
          content rather than being fixed to the viewport. */}
      <div className="absolute inset-0 flex items-start justify-center pt-24 px-4">
        <div
          className="bg-[#0A0A0A] text-white max-w-md w-full p-8 relative overflow-hidden shadow-2xl"
          data-testid="premium-gate-card"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-[#9FFF00]" />

          <div className="w-14 h-14 bg-[#9FFF00]/10 border border-[#9FFF00]/30 flex items-center justify-center mb-5">
            <Lock size={24} weight="bold" className="text-[#9FFF00]" />
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9FFF00] mb-2 flex items-center gap-2">
            <Sparkle size={12} weight="fill" /> Premium feature
          </p>

          <h2 className="text-3xl md:text-4xl font-black leading-[0.95] tracking-tight mb-4 font-heading">
            Unlock {featureName}
          </h2>

          <p className="text-white/70 text-[15px] leading-relaxed mb-6">
            {featureCopy ||
              "This feature is part of the premium plan. Upgrade now to unlock it along with every other advanced capability in Influence Detector."}
          </p>

          <Button
            onClick={handleUpgrade}
            className="bg-[#9FFF00] hover:bg-[#8AE600] text-black rounded-none font-bold w-full py-3 text-sm uppercase tracking-wider"
            data-testid="premium-gate-upgrade-btn"
          >
            See pricing plans <ArrowRight size={18} className="ml-2" />
          </Button>

          <p className="text-xs text-white/40 mt-4 text-center">
            Starter · Premium · Enterprise — pick what fits
          </p>
        </div>
      </div>
    </div>
  );
}
